import React from 'react';
import { Button } from 'tea-component';
// @ts-ignore
import aImg from './a.png';
import bImg from './b.png';
import cImg from './c.png';
import dImg from './d.png';
import { LocaleType } from '@op/types/common';
import { TipsMapType } from '@op/components/BubbleCommon';
import { history } from 'umi';

const singleKeywordTipsMap: TipsMapType = {
  zh: '明确指向本游戏的关键词，通常为各语种的游戏名称或简称。没有引入噪声数据的风险。',
  en: 'Usually the game’s name or abbreviation in various languages, a keyword that, once mentioned, clearly refers to this game. There is no risk of introducing noise data.',
};
const compoundKeywordTipsMap: TipsMapType = {
  zh: '（如需）由多个关键词通过“+”连接组成，表示“+”前后的词必须在帖文中同时出现（顺序不限）。避免因单一关键词引入噪声。',
  en: '(if needed) Composed of multiple keywords connected by a “+”, indicating that the words before and after the “+” must appear together (order does not matter). This avoids the introduction of noise from single keywords.',
};

export type ComponentKeys =
  | 'CommonText'
  | 'PreviewMirror'
  | 'FilterInfo'
  | 'ChannelShow';

function gotoSource() {
  history.push('/opinion/DataMap/OpinionsSources');
}

function gotoTopicTracking() {
  history.push('/opinion/Summary/TopicTracking');
}

const channelList = [
  {
    channel_name: 'twitter',
    replaceText: 'X(Twitter)',
  },
  'youtube',
  'tiktok',
  'instagram',
  'reddit',
  'navercafe',
  'vk',
  'bilibili',
];

const KeywordMonitoring: {
  title: string;
  description: Array<{
    component: ComponentKeys;
    filterProps?: any;
    [key: string]: any;
  }>;
} = {
  title: 'Method 1: Keyword Search Monitoring',
  description: [
    {
      component: 'CommonText',
      title: 'Monitoring Description:',
      description:
        'Using pre-configured keywords (e.g., game names) to crawl game-related posts from search results across channels.',
    },
    {
      component: 'CommonText',
      title: 'Key Features:',
      description: 'Captures posts that exactly match pre-configured keywords.',
    },
    {
      component: 'CommonText',
      title: 'Keyword Types:',
      list: [
        { text: 'Single Keyword', tipsMap: singleKeywordTipsMap },
        { text: 'Compound keywords', tipsMap: compoundKeywordTipsMap },
      ],
    },
    {
      component: 'ChannelShow',
      title: 'Common Channels:',
      channelList,
      endText: (
        <>
          See “
          <Button type="link" onClick={gotoSource}>
            Sources
          </Button>
          ” for full list.
        </>
      ),
    },
    {
      component: 'CommonText',
      title: 'Data Update:',
      description:
        'Daily updates by default. Key games can be adjusted to update hourly or set according to needs.',
    },
    {
      component: 'CommonText',
      title: 'View Configured Content:',
      description:
        'Provides a filter at the top of the page called “Monitor Method”.',
    },
    {
      component: 'PreviewMirror',
      text: 'Click to enlarge the picture.',
      img: aImg,
    },
    {
      component: 'FilterInfo',
      text: 'You can view the current game’s keyword configuration in real-time.',
      filterName: 'MonitorSelect',
    },
  ],
};

const KeywordMonitoringZh: {
  title: string;
  description: Array<{
    component: ComponentKeys;
    filterProps?: any;
    [key: string]: any;
  }>;
} = {
  title: '方法一：关键词搜索监控',
  description: [
    {
      component: 'CommonText',
      title: '监控说明：',
      description:
        '使用预设关键词（通常为游戏名），在各渠道的搜索结果中抓取本游戏相关的贴文内容。',
    },
    {
      component: 'CommonText',
      title: '监控特点:',
      description: '爬取的帖文内容一定包含（精准匹配）预设关键词。',
    },
    {
      component: 'CommonText',
      title: '关键词类型:',
      list: [
        { text: '单一关键词', tipsMap: singleKeywordTipsMap },
        { text: '复合关键词', tipsMap: compoundKeywordTipsMap },
      ],
    },
    {
      component: 'ChannelShow',
      title: '常用渠道:',
      channelList,
      endText: (
        <>
          更多可查看 “
          <Button type="link" onClick={gotoSource}>
            数据源
          </Button>
          ” 说明。
        </>
      ),
    },
    {
      component: 'CommonText',
      title: '数据更新:',
      description: '默认每日更新，重点游戏可调整为每小时更新，或根据需求设置。',
    },
    {
      component: 'CommonText',
      title: '已监控内容查看:',
      description: '页面顶部筛选器集中提供“监控方式”筛选器。',
    },
    {
      component: 'PreviewMirror',
      text: '点击放大图片',
      img: cImg,
    },
    {
      component: 'FilterInfo',
      text: '可实时查看当前游戏的关键词配置。',
      filterName: 'MonitorSelect',
    },
  ],
};

const OfficialAccount: {
  title: string;
  description: Array<{
    component: ComponentKeys;
    filterProps?: any;
    [key: string]: any;
  }>;
} = {
  title: 'Method 2: Official Account Monitoring',
  description: [
    {
      component: 'CommonText',
      title: 'Monitoring Description:',
      description:
        'Automatically crawls all content from pre-listed official accounts or communities.',
    },
    {
      component: 'CommonText',
      title: 'Key Features:',
      description:
        'Full coverage of all posts and replies under official accounts.',
    },
    {
      component: 'CommonText',
      title: 'Official Account Types:',
      list: [
        'Official Operation Account',
        'Discussion Groups/Forums',
        'KOL Monitoring',
        'Individual Comments/Danmu (Barrage)',
        'Live Stream Collection',
      ],
    },
    {
      component: 'ChannelShow',
      title: 'Common Channels:',
      channelList,
      endText: (
        <>
          See “
          <Button type="link" onClick={gotoSource}>
            Sources
          </Button>
          ” for full list.
        </>
      ),
    },
    {
      component: 'CommonText',
      title: 'Data Update:',
      description:
        'Daily updates by default. Key games can be adjusted to update hourly or set according to needs.',
    },
    {
      component: 'CommonText',
      title: 'View Configured Content:',
      description:
        'Provides a filter at the top of the page called “Monitor Method”.',
    },
    {
      component: 'PreviewMirror',
      text: 'Click to enlarge the picture.',
      img: aImg,
    },
    {
      component: 'FilterInfo',
      text: 'You can view the current game’s official account configuration in real-time.',
      filterName: 'MonitorSelect',
      filterProps: { defaultSelected: 'official-accounts' },
    },
  ],
};
const OfficialAccountZh: {
  title: string;
  description: Array<{
    component: ComponentKeys;
    filterProps?: any;
    [key: string]: any;
  }>;
} = {
  title: '方法二：官号监控',
  description: [
    {
      component: 'CommonText',
      title: '监控说明:',
      description:
        '基于提供的官号链接列表，自动爬取官方账号或社群发布的所有帖文内容。',
    },
    {
      component: 'CommonText',
      title: '监控特点:',
      description: '爬取官方账号下的所有帖文及回复内容。',
    },
    {
      component: 'CommonText',
      title: '官号类型:',
      list: [
        '官方运营账号',
        '讨论群组/论坛',
        '监控KOL',
        '单条评论/弹幕',
        '直播采集',
      ],
    },
    {
      component: 'ChannelShow',
      title: '常用渠道:',
      channelList,
      endText: (
        <>
          更多可查看 “
          <Button type="link" onClick={gotoSource}>
            数据源
          </Button>
          ” 说明。{' '}
        </>
      ),
    },
    {
      component: 'CommonText',
      title: '数据更新:',
      description: '默认每日更新，重点游戏可调整为每小时更新，或根据需求设置。',
    },
    {
      component: 'CommonText',
      title: '已监控内容查看:',
      description: '页面顶部筛选器集中提供“监控方式”筛选器。',
    },
    {
      component: 'PreviewMirror',
      text: '点击放大图片',
      img: cImg,
    },
    {
      component: 'FilterInfo',
      text: '可实时查看当前游戏的官号配置。',
      filterName: 'MonitorSelect',
      filterProps: { defaultSelected: 'official-accounts' },
    },
  ],
};

const MonitorMethodConfigEn = {
  text: 'DataBrain offers two types of monitor methods:',
  description: (
    <>
      <strong>Keyword Search Monitoring</strong> (precisely matching
      keyword-based content) and <strong>Official Account Monitoring</strong>{' '}
      (full coverage of official account activities).
    </>
  ),
  list: [KeywordMonitoring, OfficialAccount],
};

const MonitorMethodConfigZh = {
  text: 'DataBrain提供两种监控方式：',
  description: (
    <>
      <strong>关键词搜索监控</strong>（精准匹配关键词内容）与
      <strong>官号监控</strong>（全量覆盖官方动态）。
    </>
  ),
  list: [KeywordMonitoringZh, OfficialAccountZh],
};

export const MonitorMethodConfigMap: {
  [key in LocaleType]: typeof MonitorMethodConfigZh;
} = {
  'zh-CN': MonitorMethodConfigZh,
  'en-US': MonitorMethodConfigEn,
};

const TopicDefinition: {
  title: string;
  description: Array<{
    component: ComponentKeys;
    filterProps?: any;
    [key: string]: any;
  }>;
} = {
  title: 'Topic Definition',
  description: [
    {
      component: 'CommonText',
      title: 'Topic Description:',
      description: (
        <>
          Topics are identified through keyword matching by default. Users can
          customize topics and keywords using the general topic configuration
          table. <br />
          Key topics can be identified using dedicated AI models.
        </>
      ),
    },
    {
      component: 'CommonText',
      title: 'Key Features:',
      description: (
        <>
          Topics are organized in a hierarchical structure, divided into Tier 1
          (broad) and Tier 2 (specific) topics. The detailed definition of each
          Tier 2 topic is formed by a combination of Must Have and Optional
          keywords, which can be viewed on the “
          <Button type="link" onClick={gotoTopicTracking}>
            Topic Tracking
          </Button>
          ” page.
        </>
      ),
    },
    {
      component: 'CommonText',
      title: 'Topic Example:',
      list: [
        'Tier 1 Topic: Performance',
        'Tier 2 Topic: Crash',
        'Must Have Keywords : Crash, Crashed',
        'Optional Keywords : Crashed, Crash, Restarts, Restart, Interrupted, Can’t login, Can’t pickup, Quit',
      ],
    },
    {
      component: 'CommonText',
      title: 'Data Update:',
      description:
        'Supports real-time topic configuration updates (applies to new data only; does not affect historical data by default).',
    },
    {
      component: 'CommonText',
      title: 'View Configured Topics:',
      description: 'Provides a filter at the top of the page called “Topic”.',
    },
    {
      component: 'PreviewMirror',
      text: 'Click to enlarge the picture.',
      img: bImg,
    },
    {
      component: 'FilterInfo',
      text: 'You can view the current game’s topic configuration in real-time.',
      filterName: 'TopicSelect',
      hasAllKeyTopicSelectOptions: true,
    },
  ],
};

const TopicDefinitionZh: {
  title: string;
  description: Array<{
    component: ComponentKeys;
    filterProps?: any;
    [key: string]: any;
  }>;
} = {
  title: '话题定义',
  description: [
    {
      component: 'CommonText',
      title: '话题说明：',
      description: (
        <>
          话题默认基于关键词匹配识别，用户可基于默认通用话题码表自定义话题和关键词。
          <br />
          重点话题可建立单独模型识别。
        </>
      ),
    },
    {
      component: 'CommonText',
      title: '话题特点:',
      description: (
        <>
          话题采用分级结构，分为一级话题和二级话题。
          每个二级话题的详细定义由必选关键词和候选关键词组合匹配而成，可通过 “
          <Button type="link" onClick={gotoTopicTracking}>
            话题追踪
          </Button>
          ” 页面查看。
        </>
      ),
    },
    {
      component: 'CommonText',
      title: '话题示例:',
      list: [
        '一级话题：性能',
        '二级话题：崩溃',
        '必选关键词：Crash, Crashed',
        '候选关键词：Crashed, Crash, Restarts, Restart, Interrupted, Can’t login, Can’t pickup, Quit',
      ],
    },
    {
      component: 'CommonText',
      title: '数据更新：',
      description:
        '支持实时更新话题配置，确保新入库的评论数据能够基于最新的话题配置进行分类（默认不回溯历史数据）。',
    },
    {
      component: 'CommonText',
      title: '已配置话题查看：',
      description: '页面顶部筛选器集中提供“话题”筛选器。',
    },
    {
      component: 'PreviewMirror',
      text: '点击放大图片',
      img: dImg,
    },
    {
      component: 'FilterInfo',
      text: '可实时查看当前游戏的话题配置。',
      filterName: 'TopicSelect',
      hasAllKeyTopicSelectOptions: true,
    },
  ],
};

export const TopicDefinitionMap: {
  [key in LocaleType]: typeof TopicDefinition;
} = {
  'zh-CN': TopicDefinitionZh,
  'en-US': TopicDefinition,
};
